import { Image } from 'expo-image';
import { LinearGradient } from 'expo-linear-gradient';
import React from 'react';
import { StyleSheet, View, type ViewProps } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useThemePalette } from '@/config/theme';

const brandMark = require('../../../assets/images/brand-mark.png');

export interface ScreenProps extends ViewProps {
  className?: string;
  /** Apply top inset padding (off inside headers/navigators that handle it). */
  withTopInset?: boolean;
  /** The faint TASHKEELA wordmark behind content (on by default). */
  watermark?: boolean;
}

/** The brand logo, whisper-quiet behind the content. */
function Watermark() {
  return (
    <View
      className="absolute inset-x-0 items-center"
      style={{ top: '30%', pointerEvents: 'none' }}
    >
      <Image
        source={brandMark}
        style={{ width: 220, height: 220, opacity: 0.05 }}
        contentFit="contain"
      />
    </View>
  );
}

/**
 * Minimum top padding even when the platform reports no inset (web, and devices
 * with no notch) - otherwise the first header row jams against the very top
 * edge / status bar. Real notch/island devices keep their larger inset.
 */
const MIN_TOP_INSET = 16;

export function Screen({
  className,
  withTopInset = true,
  watermark = true,
  style,
  children,
  ...rest
}: ScreenProps) {
  const palette = useThemePalette();
  const insets = useSafeAreaInsets();
  return (
    <View
      className={`flex-1${className ? ` ${className}` : ''}`}
      style={[
        { backgroundColor: palette.gradBottom },
        withTopInset ? { paddingTop: Math.max(insets.top, MIN_TOP_INSET) } : null,
        style,
      ]}
      {...rest}
    >
      {/* Quiet green-black depth gradient behind everything. */}
      <LinearGradient
        colors={[palette.gradTop, palette.gradMid, palette.gradBottom]}
        locations={[0, 0.55, 1]}
        style={[StyleSheet.absoluteFill, { pointerEvents: 'none' }]}
      />
      {watermark ? <Watermark /> : null}
      {children}
    </View>
  );
}
